import React, { useState } from "react";
import { AiCapabilityMap, AiCapabilityView } from "../../types/Ai";
import classes from "./assistant.module.scss";

interface AskCapabilitiesProps {
    map: AiCapabilityMap | null;
    /** Ask this now. */
    onAsk: (question: string) => void;
    /** Put this in the box for the user to finish (it needs a name or number). */
    onFill: (question: string) => void;
}

const TOP_AREA_CHIPS = 4;

/**
 * What Ask can do for this user, in the order of their main work: questions
 * for the page they're on, their main area open, the rest one click away.
 * Built only from their own capability list, so it never offers an area
 * their role can't use; nothing shows until the list has loaded.
 */
const AskCapabilities: React.FC<AskCapabilitiesProps> = ({ map, onAsk, onFill }) => {
    const [openArea, setOpenArea] = useState<string | null>(null);
    if (!map || !map.available) return null;
    const [top, ...rest] = map.areas;

    const chip = (capability: AiCapabilityView) => (
        <button key={capability.key} type="button" className={`btn-unstyled ${classes.starter}`}
            onClick={() => (capability.send ? onAsk(capability.question) : onFill(capability.question))}
            title={capability.send ? `Ask: ${capability.question}` : "Put this in the box to finish, then press Ask"}>
            {capability.send ? capability.label : `${capability.label}…`}
        </button>
    );

    return (
        <div className={classes.capabilities}>
            {map.onThisPage.length > 0 && (
                <section className={classes.capSection} aria-label="On this page">
                    <div className={classes.capHead}><span className={classes.capLabel}>On this page</span></div>
                    <div className={classes.starters}>{map.onThisPage.map(chip)}</div>
                </section>
            )}
            {top && (
                <section className={`${classes.capSection} ${classes.capTop}`} aria-label={top.label}>
                    <div className={classes.capHead}>
                        <span className={classes.capLabel}>{top.label}</span>
                        {top.scope && <span className={classes.muted}>{top.scope}</span>}
                    </div>
                    <div className={classes.starters}>{top.capabilities.slice(0, TOP_AREA_CHIPS).map(chip)}</div>
                </section>
            )}
            {rest.length > 0 && (
                <div className={classes.capMore}>
                    <span className={classes.muted}>Also</span>
                    {rest.map((area) => {
                        const isOpen = openArea === area.key;
                        return (
                            <section key={area.key} className={classes.capSection} aria-label={area.label}>
                                <button type="button" className={`btn-unstyled ${classes.capToggle}`} aria-expanded={isOpen}
                                    onClick={() => setOpenArea(isOpen ? null : area.key)}
                                    title={isOpen ? `Hide the ${area.label} questions` : `Show what you can ask about ${area.label}`}>
                                    <span className={classes.capLabel}>{area.label}</span>
                                    {area.scope && <span className={classes.muted}>{area.scope}</span>}
                                    <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} ${classes.capChevron}`} aria-hidden="true" />
                                </button>
                                {isOpen && <div className={classes.starters}>{area.capabilities.map(chip)}</div>}
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AskCapabilities;
